/**
 * Putting a line at the number the user chooses — not at the end.
 *
 * ENQ-2026-00289 (2026-09-22): the reader missed the customer's line 3, and the only way to add
 * it made it line 8. The serial (`enquiry_row_no`, the customer's own number) is what the RFQ
 * and the quotation print, so the line has to be 3 and what was 3 has to become 4.
 *
 * Measured on `update_enquiry`: existing rows are matched by `name`, so a renumbered row keeps
 * its identity; and an explicit `idx` per row sets the order — without it a new row is appended
 * last whatever its serial. So the result carries both, in the order a person would read.
 */
interface SerialRow {
  name: string
  enquiry_row_no: string
  idx?: number
}

const numberOf = (serial: string): number | null => (/^\d+$/.test(serial.trim()) ? Number(serial) : null)

/** The serials that move down one when `serial` is taken: the unbroken run starting at it. */
export function renumberedFrom<T extends SerialRow>(rows: readonly T[], serial: string): string[] {
  const n = numberOf(serial)
  if (n === null) return []
  const used = new Set(rows.map((r) => numberOf(r.enquiry_row_no)).filter((v): v is number => v !== null))
  const out: string[] = []
  for (let k = n; used.has(k); k++) out.push(String(k))
  return out
}

export function insertLineAt<T extends SerialRow>(rows: readonly T[], line: Omit<T, 'enquiry_row_no' | 'idx'> & Partial<SerialRow>, serial: string): (T & { idx: number })[] {
  const n = numberOf(serial)
  const moving = new Set(renumberedFrom(rows, serial))
  const renumbered = rows.map((r) => (moving.has(r.enquiry_row_no) ? { ...r, enquiry_row_no: String(Number(r.enquiry_row_no) + 1) } : r))
  const placed = { ...line, enquiry_row_no: serial } as T
  // Before the first row whose number is above ours; a non-number, or a number past every
  // row's, goes last.
  const at = n === null ? -1 : renumbered.findIndex((r) => (numberOf(r.enquiry_row_no) ?? -Infinity) > n)
  const ordered = at < 0 ? [...renumbered, placed] : [...renumbered.slice(0, at), placed, ...renumbered.slice(at)]
  return ordered.map((r, i) => ({ ...r, idx: i + 1 }))
}
